import { resolveHfdKeyboardTypeFromEquipment } from "./hfdSdkRuntime";
import {
  is24GWorkMode,
  isHuafenda24GInterfaceRhythmTarget,
  isHuafendaCrossInterfaceSamePhysicalKeyboard,
  resolveEquipmentWorkMode,
} from "./hfdWirelessMusicRhythm";

/** 从 equipment 解析 keyboardType（主应用 / 键盘子应用共用） */
export function resolveRhythmKeyboardTypeForEquipment(
  equipment: Record<string, unknown> | null | undefined
): string {
  if (!equipment || typeof equipment !== "object") return "68";

  if (String(equipment.solutionRovider ?? "") === "huafenda") {
    const kt = resolveHfdKeyboardTypeFromEquipment(equipment);
    if (kt) return kt;
  }

  const raw =
    equipment.keyboardType ??
    (equipment as { keyboard_type?: unknown }).keyboard_type;
  const fromEq = String(raw ?? "").trim();
  if (fromEq && fromEq !== "undefined" && fromEq !== "null") {
    return fromEq;
  }

  const pn = String(equipment.productName ?? "");
  const dn = String(
    (equipment as { displayname?: unknown }).displayname ??
      (equipment as { displayName?: unknown }).displayName ??
      ""
  );
  const blob = `${pn} ${dn}`.toUpperCase();

  if (/\bX87\b/i.test(blob)) return "huafenda87";
  if (/\bTANK68\b/i.test(blob)) return "tank68he";
  if (/\bBAT68\b|BATTLE68|HUAFENDA68/i.test(blob)) return "huafenda68";
  if (/\bDEEP80\s*MAX\b/i.test(blob)) return "deep80max";
  if (/\bSMART875\b/i.test(blob)) return "84";
  if (/\bES68\b|DK68|DEEP68/i.test(blob)) {
    if (/EVO/i.test(blob)) return "68EVO";
    return "68";
  }
  if (/\bES98\b/i.test(blob) && !/\bES68\b/i.test(blob)) {
    return "99";
  }

  return "68";
}

export function enrichEquipmentForRhythmMatch(
  equipment: Record<string, unknown>
): Record<string, unknown> {
  const keyboardType = resolveRhythmKeyboardTypeForEquipment(equipment);
  return { ...equipment, keyboardType };
}

/** 全局律动设备 id：同 VID/PID 多型号时纳入 keyboardType */
export function buildRhythmTargetId(
  equipment: Record<string, unknown>,
  fallbackIndex?: number
): string {
  const enriched = enrichEquipmentForRhythmMatch(equipment);
  const vid = enriched.vendorId;
  const pid = enriched.productId;
  const ident = String(
    enriched.identifier ??
      enriched.uuid ??
      enriched.stableId ??
      (fallbackIndex != null ? `idx-${fallbackIndex}` : "")
  ).trim();
  const base = [vid, pid, ident].join("-");
  const kt = String(enriched.keyboardType ?? "").trim();
  return kt ? `${base}|${kt}` : base;
}

/** 主应用 connect 列表 id 无 `|keyboardType` 后缀时的 legacy 段 */
export function rhythmTargetIdLegacyBase(id: string): string {
  const s = String(id ?? "").trim();
  const pipe = s.indexOf("|");
  return pipe >= 0 ? s.slice(0, pipe) : s;
}

function normalizeRhythmDeviceIdent(
  equipment: Record<string, unknown>
): string {
  const raw =
    equipment.identifier ??
    equipment.uuid ??
    equipment.deviceId ??
    equipment.stableId;
  if (Array.isArray(raw)) {
    return raw.map((x) => String(x)).join(",");
  }
  return String(raw ?? "").trim();
}

export function equipmentMatchesRhythmTargetEquipment(
  equipment: Record<string, unknown>,
  targetEquipment: Record<string, unknown>
): boolean {
  const e1 = enrichEquipmentForRhythmMatch(equipment);
  const e2 = enrichEquipmentForRhythmMatch(targetEquipment);
  const v1 = Number(e1.vendorId);
  const p1 = Number(e1.productId);
  const v2 = Number(e2.vendorId);
  const p2 = Number(e2.productId);
  if (!Number.isFinite(v1) || !Number.isFinite(p1)) return false;
  if (v1 !== v2 || p1 !== p2) return false;

  const kt1 = String(e1.keyboardType ?? "").trim();
  const kt2 = String(e2.keyboardType ?? "").trim();
  if (kt1 && kt2 && kt1 !== kt2) return false;
  /** 同 VID/PID 下 ES98(99) 与 X98(98) 等靠 keyboardType 区分；identifier 字段来源不一不再挡匹配 */
  if (kt1 && kt2 && kt1 === kt2) return true;

  const id1 = normalizeRhythmDeviceIdent(e1);
  const id2 = normalizeRhythmDeviceIdent(e2);
  if (id1 && id2) return id1 === id2;
  return true;
}

/**
 * 子应用 / 点卡片进子应用：禁止跨方案、跨型号「宽松匹配」导致星闪 X87 绑定到华奋达 M98 上下文。
 */
export function equipmentStrictlyMatchesForRhythmIsolation(
  active: Record<string, unknown>,
  target: Record<string, unknown>
): boolean {
  const e1 = enrichEquipmentForRhythmMatch(active);
  const e2 = enrichEquipmentForRhythmMatch(target);
  const s1 = String(e1.solutionRovider ?? "").trim();
  const s2 = String(e2.solutionRovider ?? "").trim();
  if (s1 && s2 && s1 !== s2) return false;

  const v1 = Number(e1.vendorId);
  const p1 = Number(e1.productId);
  const v2 = Number(e2.vendorId);
  const p2 = Number(e2.productId);
  if (!Number.isFinite(v1) || !Number.isFinite(p1)) return false;
  if (v1 !== v2 || p1 !== p2) return false;

  const kt1 = String(e1.keyboardType ?? "").trim();
  const kt2 = String(e2.keyboardType ?? "").trim();
  if (kt1 && kt2 && kt1 !== kt2) return false;

  const id1 = normalizeRhythmDeviceIdent(e1);
  const id2 = normalizeRhythmDeviceIdent(e2);
  if (id1 && id2) return id1 === id2;
  if (id1 || id2) return false;

  return Boolean(kt1 && kt2 && kt1 === kt2);
}

/** 子应用当前卡片设备是否与律动 target 为同一台（含 X87 V2 有线/2.4G 跨接口） */
export function equipmentMatchesActiveRhythmTargetForSubApp(
  active: Record<string, unknown>,
  target: Record<string, unknown>
): boolean {
  if (equipmentStrictlyMatchesForRhythmIsolation(active, target)) return true;
  return isHuafendaCrossInterfaceSamePhysicalKeyboard(active, target);
}

export function resolveRhythmDeviceIdForEquipment(
  equipment: Record<string, unknown> | null | undefined,
  targets?: Array<{
    id: string;
    equipmentData?: Record<string, unknown>;
  }> | null
): string | null {
  if (!equipment || typeof equipment !== "object") return null;
  const eq = enrichEquipmentForRhythmMatch(equipment);
  const computed = buildRhythmTargetId(eq);
  const list = Array.isArray(targets) ? targets : [];
  if (list.length === 0) return computed;

  const exact = list.find((t) => t.id === computed);
  if (exact) return exact.id;

  const base = rhythmTargetIdLegacyBase(computed);
  const legacyExact = list.find(
    (t) =>
      t.id === base ||
      rhythmTargetIdLegacyBase(t.id) === base ||
      t.id.startsWith(`${base}|`)
  );
  if (legacyExact) return legacyExact.id;

  const strictMatches = list.filter(
    (row) =>
      row?.id &&
      row.equipmentData &&
      equipmentMatchesRhythmTargetEquipment(eq, row.equipmentData)
  );
  if (strictMatches.length === 1) return strictMatches[0].id;
  if (strictMatches.length > 1) {
    const byId =
      strictMatches.find(
        (t) =>
          t.id === computed ||
          rhythmTargetIdLegacyBase(t.id) === base ||
          t.id.startsWith(`${base}|`)
      ) ?? null;
    return byId?.id ?? null;
  }

  const crossMatches = list.filter(
    (row) =>
      row?.id &&
      row.equipmentData &&
      isHuafendaCrossInterfaceSamePhysicalKeyboard(eq, row.equipmentData)
  );
  if (crossMatches.length === 1) return crossMatches[0].id;
  if (crossMatches.length > 1) {
    const on24G =
      is24GWorkMode(resolveEquipmentWorkMode(eq)) ||
      isHuafenda24GInterfaceRhythmTarget(eq);
    const preferred = crossMatches.find((t) => {
      const row24 = isHuafenda24GInterfaceRhythmTarget(t.equipmentData!);
      return on24G ? row24 : !row24;
    });
    return (preferred ?? crossMatches[0]).id;
  }
  return null;
}

export function remapRhythmSelectedIdsForTargetListChange(
  selectedIds: Set<string>,
  prevTargets: Array<{ id: string; equipmentData?: Record<string, unknown> }>,
  nextTargets: Array<{ id: string; equipmentData?: Record<string, unknown> }>
): Set<string> {
  const next = new Set<string>();
  for (const sid of selectedIds) {
    if (nextTargets.some((t) => t.id === sid)) {
      next.add(sid);
      continue;
    }
    const prevRow = prevTargets.find((t) => t.id === sid);
    if (prevRow?.equipmentData) {
      const resolved = resolveRhythmDeviceIdForEquipment(
        prevRow.equipmentData,
        nextTargets
      );
      if (resolved) {
        next.add(resolved);
        continue;
      }
    }
    const legacyBase = sid.includes("|") ? sid.split("|")[0] : sid;
    const candidates = nextTargets.filter(
      (t) => t.id === legacyBase || t.id.startsWith(`${legacyBase}|`)
    );
    if (candidates.length === 1) next.add(candidates[0].id);
  }
  return next;
}
